import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import VideoEmbed from "@/components/app/VideoEmbed";
import PracticeCelebrateModal from "@/components/app/PracticeCelebrateModal";
import { ArrowLeft, CheckCircle2, Users } from "lucide-react";

const TARGET = 28;

export default function PracticeShortcut() {
  const { childId } = useParams();
  const nav = useNavigate();
  const [child, setChild] = useState(null);
  const [session, setSession] = useState(null);
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [rating, setRating] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [justMarked, setJustMarked] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const c = await api.get(`/children/${childId}`);
    setChild(c.data);
    const n = c.data.current_session;
    const [s, lp, l] = await Promise.all([
      api.get(`/sessions/${n}`),
      api.get(`/sessions/${n}/learn-progress?child_id=${childId}`),
      api.get(`/exercise-logs/${childId}?session_number=${n}`),
    ]);
    setSession(s.data);
    setProgress(lp.data);
    setLogs(l.data);
    // Guard: if Learn isn't complete for this session, drop them into the session page.
    if (!lp.data.complete) nav(`/child/${childId}/session/${n}`, { replace: true });
  };
  useEffect(() => { load(); }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = async () => {
    if (!child) return;
    setSaving(true); setErr(""); setJustMarked(false);
    try {
      await api.post(`/sessions/${child.current_session}/practice-log`, {
        child_id: childId,
        rating: rating || null,
        note,
      });
      setJustMarked(true);
      setRating(""); setNote("");
      const l = await api.get(`/exercise-logs/${childId}?session_number=${child.current_session}`);
      setLogs(l.data);
      // Modal owns its own auto-dismiss timer, so no local setTimeout here.
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!child || !session || !progress) return <div className="min-h-screen shell-bg p-8">Loading…</div>;
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;
  const done = logs.length;

  return (
    <div className="min-h-screen" style={{ background: tribe.bg }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm mb-6 opacity-80 hover:opacity-100" style={{ color: tribe.ink }} data-testid="practice-shortcut-back">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: tribe.subtle }}>
          Session {session.number} · {tribe.name === "Neutral" ? "" : tribe.name}
        </div>
        <h1 className="font-display text-3xl font-bold" style={{ color: tribe.accent }}>
          Practice today
        </h1>
        <p className="text-sm mt-1 opacity-80" style={{ color: tribe.ink }}>
          A shortcut into today's practice. {done}/{TARGET} logged for this session so far.
        </p>

        <div className="mt-6 rounded-3xl p-6 md:p-8" style={{ background: tribe.panel, border: `2px solid ${tribe.accent}`, color: tribe.panel_ink }} data-testid="practice-shortcut-card">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} style={{ color: tribe.panel_ink }} />
            <div className="font-display text-lg font-bold" style={{ color: tribe.panel_ink }}>Practice with Chelcie</div>
          </div>
          <p className="text-sm opacity-90 mb-4" style={{ color: tribe.panel_ink }}>
            A single timed video that walks you through this session's full set of exercises in sequence. Practice along, or just log it — either counts.
          </p>
          <VideoEmbed
            value={session.practice_with_chelcie?.video_url}
            title={`Practice with Chelcie — Session ${session.number}`}
            placeholder={`Practice with Chelcie · Session ${session.number} — combined timed practice`}
            theme={{ borderColor: tribe.subtle, background: tribe.panel_soft, color: tribe.panel_ink }}
            testid="practice-shortcut-video"
          />

          <div className="mt-6 rounded-2xl p-5" style={{ background: tribe.panel_soft, border: `1px dashed ${tribe.subtle}`, color: tribe.panel_ink }}>
            <div className="mb-3">
              <div className="text-sm font-semibold" style={{ color: tribe.panel_ink }}>Practiced today?</div>
              <div className="text-xs opacity-90 mt-0.5" style={{ color: tribe.panel_ink }}>Log one practice — counts toward this session's {TARGET} target, same as if you'd gone through the full session page.</div>
            </div>

            <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: tribe.panel_ink }}>How did it go?</div>
            <div className="flex gap-2 flex-wrap">
              {["easy", "okay", "hard"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRating(r)}
                  data-testid={`practice-shortcut-rate-${r}`}
                  className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition capitalize"
                  style={rating === r
                    ? { background: tribe.subtle, borderColor: tribe.subtle, color: "#1E2A3A" }
                    : { borderColor: tribe.subtle, color: tribe.panel_ink, background: "transparent" }}
                >
                  {r}
                </button>
              ))}
            </div>

            <textarea
              placeholder="Optional note…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="practice-shortcut-note"
              rows={2}
              className="mt-3 w-full px-3 py-2 rounded-xl text-sm border-2"
              style={{ background: "rgba(255,254,249,0.1)", color: tribe.panel_ink, borderColor: tribe.subtle }}
            />

            <button
              onClick={mark}
              disabled={saving}
              data-testid="practice-shortcut-mark-complete"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold shrink-0 disabled:opacity-40"
              style={{ background: tribe.subtle, color: "#1E2A3A" }}
            >
              <CheckCircle2 size={16} /> {saving ? "Logging…" : "Mark Practice Complete"}
            </button>

            {err && <div className="mt-3 text-sm text-rose-300" data-testid="practice-shortcut-err">{err}</div>}
          </div>
        </div>
      </div>
      <PracticeCelebrateModal
        open={justMarked}
        onClose={() => setJustMarked(false)}
        childName={child?.is_self ? "" : child?.name}
        tribe={tribe}
        practicesDone={done}
        target={TARGET}
        nextSession={Math.min(12, session.number + 1)}
      />
    </div>
  );
}
